<template>
  <div v-if="announcements.length" class="flex justify-between h-full">
    <div
      id="announcement-panel"
      class="relative h-full w-full"
      :aria-live="showInfoTab ? 'polite' : 'off'"
    >
      <TransitionGroup name="fade" mode="in-out">
        <div
          v-for="(announcement, i) in announcements"
          v-show="currentIndex === i"
          :key="`${announcement.id}`"
          role="group"
          aria-roledescription="slide"
          class="a-cover md:flex text-white"
        >
          <img
            v-if="!phone && announcement.image"
            :src="announcement.image.sizes.thumbnail"
            alt="announcement.image.alt"
            class="h-24 w-24"
          />
          <div class="h-full px-3 py-2">
            <h2 class="text-sm mb-2">
              <a
                :href="utmHref(announcement.link, 'tier-2-cta')"
                class="block font-medium underline focus-a w-56"
                :aria-label="`${htmlDecode(announcement.title)}`"
                v-html="announcement.title"
              />
            </h2>
            <p
              v-if="announcement.description"
              class="text-sm"
              v-html="`${announcement.description.substring(0, 95)}...`"
            />
          </div>
        </div>
      </TransitionGroup>
    </div>
    <div
      class="flex flex-col items-center justify-between h-full w-12 p-1 text-white"
    >
      <Button
        v-if="announcements.length > 1"
        type="button"
        title="View previous announcement"
        icon="ChevronIcon"
        iconsize="25px"
        iconrotation="rotate-90"
        class="h-10 w-10 flex-c focus-a"
        aria-controls="announcement-panel"
        @buttonClick="handlePrevNext('prev')"
        @keyup.esc.native="handleEscape"
      />
      <Button
        v-if="announcements.length > 1"
        type="button"
        title="View next announcement"
        icon="ChevronIcon"
        iconsize="25px"
        iconrotation="rotate-270"
        class="h-10 w-10 flex-c focus-a"
        aria-controls="announcement-panel"
        @buttonClick="handlePrevNext('next')"
        @keyup.esc.native="handleEscape"
      />
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import Button from '@/components/buttons/Button.vue'
import { limitString } from '@/mixins'

export default {
  name: 'Announcements',

  components: {
    Button
  },

  mixins: [limitString],

  data() {
    return {
      currentIndex: 0,
      viewedAnnouncements: []
    }
  },

  computed: {
    ...mapState(['touch', 'mobile', 'showInfoTab', 'phone']),
    ...mapGetters(['announcements']),

    showUnseenAnnouncementsAlert() {
      const ids = this.announcements.map(({ id }) => id)

      return !ids.every(id => this.viewedAnnouncements.includes(id))
    }
  },

  watch: {
    showInfoTab: {
      immediate: true,
      handler(val) {
        if (val) {
          this.$store.dispatch('fetchAnnouncements')
        }
      }
    },

    showUnseenAnnouncementsAlert: {
      immediate: true,
      handler(value) {
        this.$emit('showUnseenAnnouncementsAlert', value)
      }
    },

    viewedAnnouncements: {
      immediate: false,
      handler() {
        localStorage.setItem(
          'viewedAnnouncementIds',
          JSON.stringify(this.viewedAnnouncements)
        )
      }
    }
  },

  created() {
    const storedAnnouncementIds = localStorage.getItem('viewedAnnouncementIds')

    if (storedAnnouncementIds) {
      this.viewedAnnouncements = JSON.parse(storedAnnouncementIds)
    }
  },

  methods: {
    handlePrevNext(direction) {
      const length = this.announcements.length
      const index = this.currentIndex

      switch (direction) {
        case 'next':
          this.currentIndex = (index + 1) % length
          break
        case 'prev':
          this.currentIndex = (index === 0 && length - 1) || index - 1
          break
        default:
          this.currentIndex = index
      }

      this.handleViewedAnnouncements()
    },

    handleViewedAnnouncements() {
      const currentAnnouncementId = this.announcements[this.currentIndex].id

      if (!this.viewedAnnouncements.includes(currentAnnouncementId)) {
        this.viewedAnnouncements.push(currentAnnouncementId)
      }
    },

    handleEscape() {
      document.getElementById('announcement-toggle').focus()
      this.$store.commit('toggleInfoTabState')
    }
  }
}
</script>
